import { useEffect, useId, useRef, useState } from 'react';

import ModalActions from 'components/task-item/modal-actions';
import { TaskItemProps, useTaskItem } from 'components/task-item/useTaskItem';
import { FOCO_VISIVEL } from 'utils/foco';

import { reduzirParticipacao, sairDaRealizacao } from 'services/participacao.service';
import { pilulaDaRealizacao, progressoDaTrilha } from '../pilula-da-realizacao';
import { useAvisoDaPilula } from '../useAvisoDaPilula';
import { FaseDoCiclo } from '../realizacoes-do-semestre';
import AcoesDoParticipante from './AcoesDoParticipante';
import { etapasDaAgenda } from './AgendaDaRealizacao';
import CabecalhoDaRealizacao, { autorDaRealizacao } from './CabecalhoDaRealizacao';
import CamposDaRealizacao, { ATUACOES, percentualDaAtuacao } from './CamposDaRealizacao';
import { alternarMaisInformacoes, BotaoMaisInformacoes, PainelMaisInformacoes } from './MaisInformacoes';
import TrilhaDaRealizacao from './TrilhaDaRealizacao';

export const FALHA_NA_ACAO = 'Não foi possível concluir. Tente novamente.';
export const AVISO_DE_REDUCAO = 'Seu percentual de atuação foi atualizado.';
export const AVISO_DE_SAIDA = 'Você saiu desta realização.';

type Painel = 'reduzir' | 'sair' | null;

const ACAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 text-[10.5px] font-bold uppercase tracking-[0.04em] hover:underline disabled:cursor-not-allowed disabled:opacity-50 ${FOCO_VISIVEL}`;

export interface CartaoDeParticipacaoProps {
  data: any;
  /** Fase mensal em que a realização foi cadastrada. */
  fase: FaseDoCiclo | undefined;
  rotuloDaFase: string;
  playerInfo: any;
  /** Gravação confirmada (`status === 'OK'`): a página anuncia o aviso e relê o ciclo. */
  onConcluida: (aviso: string) => void;
  /** Depois de uma ação dos modais do "Mais informações" ou quando ele pede a leitura completa: a página relê o ciclo. */
  onRecarregar: () => void;
  /** Pílula do cartão (RDV-14), avisada sempre que muda, para as pílulas do topo da página. */
  onPilula?: (pilula: string) => void;
}

/**
 * Cartão de uma realização em que o usuário participa (RDV-16 a RDV-21, RDN-12…RDN-17): Reduzir e Sair aparecem nas mesmas condições
 * do `TaskItem` (`canDecreaseParticipation`/`canExcludeParticipation` para a linha do próprio usuário) e gravam com o
 * mesmo payload dos modais de hoje.
 */
export default function CartaoDeParticipacao({
  data,
  fase,
  rotuloDaFase,
  playerInfo,
  onConcluida,
  onRecarregar,
  onPilula,
}: CartaoDeParticipacaoProps) {
  const modelo = useTaskItem({ data, cycle: fase as TaskItemProps['cycle'], playerInfo, onReload: onRecarregar });
  const chave: string | undefined = modelo.user?._id;
  // O `TaskItem` só oferece as ações na linha do usuário entre os participantes (`target`).
  const eu = Array.isArray(data.target) ? data.target.find((pessoa: any) => pessoa._id === chave) : undefined;
  const percentual = chave ? percentualDaAtuacao(modelo.getUserTeam(chave)?.percent) : undefined;
  const podeReduzir = !!eu && modelo.canDecreaseParticipation(eu);
  const podeSair = !!eu && modelo.canExcludeParticipation(eu);
  const etapas = etapasDaAgenda(modelo);
  const pilula = pilulaDaRealizacao(etapas);

  const [painel, setPainel] = useState<Painel>(null);
  const [enviando, setEnviando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const tituloId = useId();
  const detalhesId = useId();
  const textoDoPainel = useRef<HTMLParagraphElement>(null);
  const botaoReduzir = useRef<HTMLButtonElement>(null);
  const botaoSair = useRef<HTMLButtonElement>(null);
  const painelAnterior = useRef<Painel>(null);

  useAvisoDaPilula(pilula, onPilula);

  // O botão que abre o painel sai da tela: o foco vai ao texto do painel e, ao cancelar, volta a quem o abriu.
  useEffect(() => {
    if (painel) {
      textoDoPainel.current?.focus();
    } else if (painelAnterior.current) {
      (painelAnterior.current === 'reduzir' ? botaoReduzir : botaoSair).current?.focus();
    }
    painelAnterior.current = painel;
  }, [painel]);

  function abrir(qual: Painel) {
    setFalhou(false);
    setPainel(qual);
  }

  // Fechar durante a gravação esconderia o desfecho dela; Cancelar e Voltar ficam `aria-disabled` como os demais (RDN-64).
  function fecharPainel() {
    if (enviando) return;
    abrir(null);
  }

  async function gravar(acao: () => Promise<boolean>, aviso: string) {
    if (enviando) return;
    setEnviando(true);
    setFalhou(false);
    const concluiu = await acao();
    setEnviando(false);

    if (concluiu) {
      // A página relê o ciclo e anuncia o aviso; não há botão de origem para onde devolver o foco.
      painelAnterior.current = null;
      setPainel(null);
      onConcluida(aviso);
    } else {
      setFalhou(true);
    }
  }

  return (
    <article aria-labelledby={tituloId} className="overflow-hidden rounded-jj-lg bg-brand-white shadow-jj-01">
      <div className="flex flex-col gap-3.5 px-5 pb-4 pt-[18px]">
        <CabecalhoDaRealizacao
          tituloId={tituloId}
          titulo={data.title}
          autor={autorDaRealizacao(data)}
          propria={false}
          rotuloDaFase={rotuloDaFase}
          cadastradaEm={data.created}
          pilula={pilula}
          pontos={modelo.points}
        />
        <TrilhaDaRealizacao etapas={etapas} progresso={progressoDaTrilha(etapas)} />
        <CamposDaRealizacao data={data} percentual={percentual} />

        {painel === 'reduzir' && typeof percentual === 'number' && (
          <div className="flex flex-col gap-2.5 rounded-jj-md bg-surface-100 px-4 py-3.5">
            <p ref={textoDoPainel} tabIndex={-1} className="m-0 text-[11.5px] leading-[1.55] text-content outline-none">
              Escolha um percentual menor que o atual. Não é possível aumentar o seu percentual de atuação.
            </p>
            <div role="group" aria-label="Novo percentual de atuação" className="grid grid-cols-4 gap-1.5">
              {ATUACOES.map(atuacao => {
                const bloqueado = atuacao.valor >= percentual;
                const atual = atuacao.valor === percentual;
                return (
                  <button
                    key={atuacao.valor}
                    type="button"
                    // Durante a gravação o botão fica `aria-disabled` para o foco não cair no `body`.
                    disabled={bloqueado}
                    aria-disabled={enviando || undefined}
                    aria-current={atual ? 'true' : undefined}
                    onClick={() => gravar(() => reduzirParticipacao(data._id, atuacao.valor, chave as string), AVISO_DE_REDUCAO)}
                    className={`flex flex-col items-center gap-0.5 rounded-jj-md border-2 px-1 py-2 disabled:cursor-not-allowed ${
                      atual ? 'border-brand-green bg-brand-white' : 'border-surface-200 bg-surface-100'
                    } ${bloqueado ? 'text-content-soft' : 'text-brand-blue-dark'} ${bloqueado && !atual ? 'opacity-40' : ''} ${FOCO_VISIVEL}`}
                  >
                    <span className="font-display text-sm font-extrabold leading-none">{`${atuacao.valor}%`}</span>
                    <span className="text-[9px] leading-tight">{atuacao.nome}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              aria-disabled={enviando || undefined}
              onClick={fecharPainel}
              className={`${ACAO_DE_TEXTO} self-start text-content aria-disabled:opacity-50`}
            >
              Cancelar
            </button>
          </div>
        )}

        {painel === 'sair' && (
          <div className="flex flex-wrap items-center gap-3.5 rounded-jj-md bg-brand-pink-light px-4 py-3.5">
            <p
              ref={textoDoPainel}
              tabIndex={-1}
              className="m-0 flex-[1_1_260px] text-[11.5px] leading-[1.55] text-brand-navy outline-none"
            >
              <b>Tem certeza?</b> Ao sair, você deixa de receber os pontos e o Selo desta realização. Só quem cadastrou pode
              incluir você de novo.
            </p>
            <div className="flex gap-2.5">
              <button
                type="button"
                aria-disabled={enviando || undefined}
                onClick={fecharPainel}
                className={`rounded-jj-md border-0 bg-brand-white px-4 py-[9px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark aria-disabled:opacity-50 ${FOCO_VISIVEL}`}
              >
                Voltar
              </button>
              {/* SPEC_DEVIATION: fundo `urgent` no lugar do `brand-pink` do design (texto branco a 2,6:1 no rosa). */}
              <button
                type="button"
                aria-disabled={enviando || undefined}
                onClick={() => gravar(() => sairDaRealizacao(data._id, chave as string, modelo.ecoa), AVISO_DE_SAIDA)}
                className={`rounded-jj-md border-0 bg-urgent px-4 py-[9px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-white aria-disabled:opacity-50 ${FOCO_VISIVEL}`}
              >
                Sair da realização
              </button>
            </div>
          </div>
        )}

        <PainelMaisInformacoes
          id={detalhesId}
          modelo={modelo}
          etapas={etapas}
          // A linha do próprio usuário já tem Reduzir/Sair no cartão; as demais seguem o `TaskItem` (gestor do participante).
          acoesDoParticipante={pessoa => pessoa._id !== chave && <AcoesDoParticipante pessoa={pessoa} modelo={modelo} />}
        />

        {/* Sempre montada, sem altura quando vazia: o leitor de tela anuncia o aviso quando o texto entra (RDN-63). */}
        <p role="status" className="m-0 -mt-3.5 text-xs font-bold text-urgent">
          {falhou ? FALHA_NA_ACAO : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 border-t border-surface-200 bg-surface-100 px-5 py-3">
        {/* "Sair" segue só o `canExcludeParticipation`, como no `TaskItem`: não depende de haver percentual (RDN-15). */}
        {!painel && podeReduzir && typeof percentual === 'number' && (
          <button
            ref={botaoReduzir}
            type="button"
            onClick={() => abrir('reduzir')}
            className={`${ACAO_DE_TEXTO} text-brand-blue`}
          >
            Reduzir meu percentual
          </button>
        )}
        {!painel && podeSair && (
          // SPEC_DEVIATION: `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
          <button ref={botaoSair} type="button" onClick={() => abrir('sair')} className={`${ACAO_DE_TEXTO} text-urgent`}>
            Sair da realização
          </button>
        )}
        <BotaoMaisInformacoes aberto={modelo.oppened} controla={detalhesId} onClick={() => alternarMaisInformacoes(modelo)} />
      </div>

      <ModalActions
        callback={modelo.callBackActions}
        id={data._id}
        status={modelo.status}
        data={data}
        playerInfo={playerInfo}
        validateTeamMember={modelo.validateTeamMember}
        activeEvaluationStep={modelo.activeEvaluationStep as number}
        bonus={modelo.bonus}
        userToChange={modelo.userToChange}
        ecoa={modelo.ecoa}
      />
    </article>
  );
}
